import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { uploadToCloudinary } from '../../utils/cloudinary';
import { validateImageFile } from '../../utils/imageValidation';

/**
 * Unified hook for image upload functionality
 * Provides consistent upload logic, error handling, and state management
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.uploadFolder - Cloudinary folder for uploads
 * @param {Object} options.maxSize - Max dimensions {width, height}
 * @param {number} options.maxSizeMB - Max file size in MB
 * @param {Function} options.onSuccess - Success callback
 * @param {Function} options.onError - Error callback
 * @param {boolean} options.allowMultiple - Allow multiple images
 * @param {boolean} options.requireCrop - Require cropping before upload
 * @param {string} options.cropShape - 'rect' or 'round'
 * @param {number} options.aspectRatio - Crop aspect ratio (0 = free form)
 */
export const useImageUpload = (options = {}) => {
  const {
    uploadFolder = 'uploads',
    maxSize = { width: 1600, height: 1600 },
    maxSizeMB = 10,
    onSuccess,
    onError,
    allowMultiple = false,
    requireCrop = false,
    cropShape = 'rect',
    aspectRatio = 0
  } = options;

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedUrls, setUploadedUrls] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [cropConfig, setCropConfig] = useState({});

  // Validate and prepare files for upload/crop
  const handleFileSelection = useCallback((files) => {
    const fileList = Array.isArray(files) ? files : Array.from(files || []);
    
    if (!allowMultiple && fileList.length > 1) {
      toast.error('Only one image allowed');
      return false;
    }

    const validFiles = [];
    const errors = [];

    fileList.forEach((file, index) => {
      const validation = validateImageFile(file, { maxSizeMB });
      if (validation.ok) {
        validFiles.push(file);
      } else {
        errors.push(`File ${index + 1}: ${validation.error}`);
      }
    });

    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
      return false;
    }

    setSelectedFiles(validFiles);
    
    // Create preview URLs
    const previews = validFiles.map(file => URL.createObjectURL(file));
    setPreviewUrls(prev => {
      // Cleanup old URLs
      prev.forEach(url => URL.revokeObjectURL(url));
      return previews;
    });

    // If cropping required, open crop modal for first file
    if (requireCrop && validFiles.length > 0) {
      setImageToCrop(validFiles[0]);
      setCropConfig({
        cropShape,
        aspectRatio,
        uploadFolder,
        maxSize
      });
      setShowCropModal(true);
    }

    return true;
  }, [allowMultiple, maxSizeMB, requireCrop, cropShape, aspectRatio, uploadFolder, maxSize]);

  // Direct upload without cropping
  const uploadFiles = useCallback(async (files = selectedFiles) => {
    if (!files.length) return [];

    try {
      setIsUploading(true);
      setUploadProgress(0);
      
      const urls = [];
      const totalFiles = files.length;
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const url = await uploadToCloudinary(file, {
          folder: uploadFolder,
          compress: true,
          maxWidth: maxSize.width,
          maxHeight: maxSize.height,
          quality: 0.8
        });
        
        urls.push(url);
        setUploadProgress(Math.round(((i + 1) / totalFiles) * 100));
      }
      
      setUploadedUrls(urls);
      onSuccess?.(allowMultiple ? urls : urls[0]);
      
      return urls;
    } catch (error) {
      console.error('Upload failed:', error);
      const errorMessage = error?.message?.includes('network') 
        ? 'Network error. Please check your connection and try again.'
        : 'Upload failed. Please try again.';
      
      toast.error(errorMessage);
      onError?.(error);
      throw error;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, [selectedFiles, uploadFolder, maxSize, onSuccess, onError, allowMultiple]);

  // Handle crop completion
  const handleCropComplete = useCallback(async (croppedBlob) => {
    try {
      setShowCropModal(false);
      setImageToCrop(null);
      
      if (!croppedBlob) return;
      
      // Upload cropped image
      const url = await uploadToCloudinary(croppedBlob, {
        folder: uploadFolder,
        compress: true,
        maxWidth: maxSize.width,
        maxHeight: maxSize.height,
        quality: 0.8
      });
      
      setUploadedUrls([url]);
      onSuccess?.(url);
      
      return url;
    } catch (error) {
      console.error('Crop upload failed:', error);
      toast.error('Failed to upload cropped image. Please try again.');
      onError?.(error);
      throw error;
    }
  }, [uploadFolder, maxSize, onSuccess, onError]);

  // Reset all states
  const reset = useCallback(() => {
    setSelectedFiles([]);
    setUploadedUrls([]);
    setIsUploading(false);
    setUploadProgress(0);
    setShowCropModal(false);
    setImageToCrop(null);
    
    // Cleanup preview URLs
    previewUrls.forEach(url => {
      try {
        URL.revokeObjectURL(url);
      } catch {}
    });
    setPreviewUrls([]);
  }, [previewUrls]);

  // Remove specific file
  const removeFile = useCallback((index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => {
      const urlToRevoke = prev[index];
      if (urlToRevoke) {
        try {
          URL.revokeObjectURL(urlToRevoke);
        } catch {}
      }
      return prev.filter((_, i) => i !== index);
    });
    setUploadedUrls(prev => prev.filter((_, i) => i !== index));
  }, []);

  return {
    // State
    isUploading,
    uploadProgress,
    selectedFiles,
    uploadedUrls,
    previewUrls,
    showCropModal,
    imageToCrop,
    cropConfig,
    
    // Actions
    handleFileSelection,
    uploadFiles,
    handleCropComplete,
    reset,
    removeFile,
    
    // Crop modal helpers
    closeCropModal: () => {
      setShowCropModal(false);
      setImageToCrop(null);
    }
  };
};

export default useImageUpload;
